import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"

function unwrapSocialNetworkTypesPayload(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.types)) return data.types
  if (Array.isArray(data?.content)) return data.content
  return []
}

// Async thunk for fetching social network types
export const fetchSocialNetworkTypes = createAsyncThunk(
  "socialNetworkTypes/fetch",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required")
      }

      const data = await get("social-network-types")
      return unwrapSocialNetworkTypesPayload(data)
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch social network types")
    }
  },
  {
    condition: (_, { getState }) => {
      const slice = getState().socialNetworkTypes ?? {}
      if (slice.status === "loading") return false
      if (slice.status === "succeeded" && Array.isArray(slice.types) && slice.types.length > 0) {
        return false
      }
      return true
    },
  },
)

const initialState = {
  types: [],
  selectedTypes: [],
  status: "idle", // idle | loading | succeeded | failed
  error: null,
}

const socialNetworkTypesSlice = createSlice({
  name: "socialNetworkTypes",
  initialState,
  reducers: {
    setSelectedTypes: (state, action) => {
      state.selectedTypes = action.payload
    },
    toggleSelectedType: (state, action) => {
      const typeId = action.payload
      const index = state.selectedTypes.findIndex((type) => type.id === typeId)

      if (index >= 0) {
        // Remove if already selected
        state.selectedTypes.splice(index, 1)
      } else {
        // Add if not selected
        const networkType = state.types.find((type) => type.id === typeId)
        if (networkType) {
          state.selectedTypes.push(networkType)
        }
      }
    },
    clearSelectedTypes: (state) => {
      state.selectedTypes = []
    },
    clearTypes: (state) => {
      state.types = []
      state.selectedTypes = []
      state.status = "idle"
      state.error = null
    },
    clearError: (state) => {
      state.error = null
    },
    resetOnLogout: (state) => {
      return initialState
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSocialNetworkTypes.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchSocialNetworkTypes.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.types = unwrapSocialNetworkTypesPayload(action.payload)
      })
      .addCase(fetchSocialNetworkTypes.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
  },
})

export const { setSelectedTypes, toggleSelectedType, clearSelectedTypes, clearTypes, clearError, resetOnLogout } =
  socialNetworkTypesSlice.actions

export default socialNetworkTypesSlice.reducer
